import { useState, useCallback, useEffect, useRef } from 'react';

// ----------------------------
// useDebounce Hook
// ----------------------------
export function useDebounce<T>(value: T, delay = 1000): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  const debounce = (func: (...args: any[]) => void) => {
    let timer: ReturnType<typeof setTimeout> | null;
    return (...args: any[]) => {
      const context = this;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        func.apply(context, args);
      }, delay);
    };
  };

  const startTimer = useCallback(
    debounce((val: T) => {
      setDebouncedValue(val);
    }),
    [],
  );

  useEffect(() => {
    startTimer(value);
  }, [value]);

  return debouncedValue;
}

// ----------------------------
// useDebouncedEffect Hook
// ----------------------------
export function useDebouncedEffect(
  callback: () => void,
  deps: React.DependencyList,
  delay: number,
): void {
  useEffect(() => {
    let cleanupFn;

    const handler = setTimeout(() => {
      const maybeCleanup = callback();
      if (typeof maybeCleanup === 'function') {
        cleanupFn = maybeCleanup;
      }
    }, delay);

    return () => {
      clearTimeout(handler);
      if (cleanupFn) cleanupFn();
    };
  }, deps);
}

// ----------------------------
// useDebouncedMemo Hook (Generic)
// ----------------------------
export function useDebouncedMemo<T>(
  factory: () => T,
  deps: React.DependencyList,
  delay: number,
  defaultValue: T | null = null,
): T | null {
  const [debouncedValue, setDebouncedValue] = useState<T | null>(defaultValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      const newValue = factory();
      setDebouncedValue(newValue);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, deps);

  return debouncedValue;
}

// ----------------------------
// useThrottle Hook
// ----------------------------
export function useThrottle<T extends (...args: any[]) => void>(
  callback: T,
  delay: number,
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const throttledFunction = (...args: Parameters<T>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };

  return throttledFunction;
}
